import React from 'react';
import { Badge, Row, Button } from 'reactstrap';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { throttle } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileInvoiceDollar,
    faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    booleanCellRenderer,
    createClientSideGridOptions,
    TextFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import CommonContext, { agGridConstants, ApiRoutes, ChargeTypeResourceTypes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { SmallButton, toasty } from '../common/forms/FormElements';

export default class ChargeTypeSelect extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            customerId: '',
            chargeTypes: [],
            loading: true,
            omissions: [],
            refreshing: false,
            rowData: [],
            selectedRows: [],
            show: false,
        };
        this.yesNoCellRenderer = this.yesNoCellRenderer.bind(this);
        this.onSelectionChanged = this.onSelectionChanged.bind(this);
        this.setupGrid = this.setupGrid.bind(this);
        this.refreshChargeTypes = throttle(this.refreshChargeTypes, 2000);
    }

    componentDidMount() {
        this.populateState();
    }

    onAdd = () => {
        const { selectedRows } = { ...this.state };
        this.setState({ show: false }, () => {
            this.props.onAddCallback(selectedRows);
        });
        this.context.setFormOpened(false);
    };

    onClose = () => {
        this.setState({ show: false, formValidated: false });
        this.context.setFormOpened(false);
    };

    onSelectionChanged = (event) => {
        const selections = event.api.getSelectedRows() ?? [];
        this.setState({ selectedRows: selections });
    };

    getChargeTypes = async (id, omissions, isProjectContract) => {
        const { billableOnly } = { ...this.props };

        let chargeTypes = [];
        if (billableOnly) {
            chargeTypes = await httpClient.js(
                ApiRoutes.chargeType.byCustomerBillable(id),
            );
        } else {
            chargeTypes = await httpClient.js(
                ApiRoutes.chargeType.byCustomer(id),
            );
        }

        chargeTypes = chargeTypes.filter((ct) => !omissions.includes(ct.id)) ?? [];

        if (isProjectContract === false) {
            chargeTypes = chargeTypes.filter((ct) => ct.resourceTypeId !== ChargeTypeResourceTypes.Rental);
        }

        this.setState({
            rowData: chargeTypes,
            selectedRows: [],
            refreshing: false,
            loading: false,
        });
    };

    setupGrid = ({
        userGroups,
        applicationGroups,
        equipmentTypes,
        units,
    }) => {
        const gridOptions = createClientSideGridOptions(this);

        const filterOptions = { ...TextFilterDefaults, suppressFilterButton: true };

        gridOptions.headerHeight = 28;
        gridOptions.rowMultiSelectWithClick = true;
        gridOptions.overlayNoRowsTemplate = 'No charge types to display. You\'ve either selected all the available charge types, or no charge types were found.';

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
        };

        const unitsFilterParams = {
            suppressFilterButton: true,
            options: units,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const groupFilterParams = {
            suppressFilterButton: true,
            options: applicationGroups,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const employeesFilterParams = {
            suppressFilterButton: true,
            options: userGroups,
            optionsLabel: 'groupName',
            optionsValue: 'id',
        };
        const equipmentFilterParams = {
            suppressFilterButton: true,
            options: equipmentTypes,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const billableFilterParams = {
            suppressFilterButton: true,
            options: [{ label: 'Yes', value: true }, { label: 'No', value: false }],
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        gridOptions.columnDefs = [
            {
                colId: 'Description',
                sortable: true,
                headerName: 'Description',
                field: 'description',
                filter: agGridConstants.columnFilterTypes.text,
                floatingFilter: true,
                filterParams: filterOptions,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                sort: { direction: 'asc', priority: 0 },
                cellRenderer: (params) => {
                    const badge = params.data.isException
                        ? (<Badge
                                color="warning"
                                className="exception-badge ml-2"
                        >Exception</Badge>)
                        : '';
                    return (
                        <div className="d-flex flex-row flex-nowrap align-items-center">
                            {params.data.description}
                            {badge}
                        </div>
                    );
                },
            },
            {
                width: 100,
                flex: 0,
                colId: 'Units',
                sortable: true,
                headerName: 'Units',
                field: 'unitsId',
                valueFormatter: ({ data }) => data?.unitsName ?? '',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: unitsFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: unitsFilterParams,
            },
            {
                sortable: true,
                headerName: 'Group',
                field: 'applicationGroupId',
                valueFormatter: ({ data }) => data?.appliesToGroup ?? '',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: groupFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: groupFilterParams,
            },
            {
                sortable: true,
                headerName: 'Employees',
                field: 'appliesToUserGroupIds',
                valueFormatter: ({ data }) => data?.appliesToUsers ?? '',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: employeesFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: employeesFilterParams,
            },
            {
                sortable: true,
                headerName: 'Equipment',
                field: 'appliesToEquipmentTypeIds',
                valueFormatter: ({ data }) => data?.appliesToEquipment ?? '',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: equipmentFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: equipmentFilterParams,
            },
            {
                width: 100,
                flex: 0,
                sortable: true,
                headerName: 'Billable?',
                field: 'isBillable',
                cellRenderer: this.yesNoCellRenderer,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: billableFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: billableFilterParams,
            },
        ];

        return { gridOptions };
    };

    open = async (id, omissions, isProjectContract) => {
        const customerId = id;
        await this.getChargeTypes(customerId, omissions, isProjectContract);
        this.context.setFormOpened(true);
        this.setState({
            show: true,
            customerId,
            omissions: [...omissions],
        });
    };

    async populateState() {
        const [userGroups, applicationGroups, equipmentTypes, units] = await Promise.all([
            httpClient.js(ApiRoutes.auth.groups()),
            httpClient.js(ApiRoutes.userGroup.all()),
            httpClient.js(ApiRoutes.typeAheads.equipmentTypes()),
            httpClient.js(ApiRoutes.chargeTypeUnit.all()),
        ]);

        const { gridOptions } = this.setupGrid({
            userGroups,
            applicationGroups,
            equipmentTypes,
            units,
        });

        gridOptions.rowData = [];

        this.setState({
            loading: false,
            gridOptions,
        });
    }

    refreshChargeTypes = async () => {
        const { customerId, omissions } = { ...this.state };
        await this.getChargeTypes(customerId, omissions);
        toasty.success(
            'Data refreshed',
            'Charge type data reloaded successfully.',
        );
    };

    yesNoCellRenderer = booleanCellRenderer;

    render() {
        const { gridOptions, formValidated, selectedRows } = this.state;
        const selectionCount = (selectedRows ?? []).length
            ? ` (${selectedRows.length})`
            : '';

        return (
            <SlideForm
                size="col-xl-6 col-md-12 col-sm-12"
                loading={this.state.loading}
                show={this.state.show}
                id={this.props.id}
                formIcon={faFileInvoiceDollar}
                formTitle={this.props.title ?? 'Add Charge Types to Contract'}
                formRef={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onClose={this.onClose}
            >
                <div className="w-100 d-flex flex-row align-items-center pt-1 pb-1">
                    <div className="flex-fill d-flex flex-row align-items-center">
                        <FontAwesomeIcon
                            icon={faQuestionCircle}
                            className="mr-1"
                        />
                        <small>
                            You may multiselect by clicking on individual rows.
                        </small>
                    </div>
                    <div className="flex-fill d-flex flex-row align-items-center justify-content-end">
                        <SmallButton
                            disabled={!!this.state.refreshing}
                            onClick={this.refreshChargeTypes}
                        >
                            <i className="fa fa-sync-alt fa-md mr-2" />
                            Refresh Charge Type Data
                        </SmallButton>
                    </div>
                </div>
                <div className="pt-0 flex-fill ag-theme-alpine datatable">
                    <AgGridReact
                        gridOptions={gridOptions}
                        rowData={this.state.rowData}
                    />
                </div>
                <Row className="d-flex flex-row justify-content-end pt-2 pl-0 pr-0 ml-0 mr-0 text-right w-100">
                    <Button
                        disabled={!(this.state.selectedRows ?? []).length}
                        size="sm"
                        type="button"
                        color="primary"
                        onClick={this.onAdd}
                    >
                        {`Add Selected${selectionCount}`}
                    </Button>
                </Row>
            </SlideForm>
        );
    }
}
